import React, { useEffect, useMemo, useRef, useState } from 'react';
import api, { getActiveOperatingGroupId } from '../api/api';
import { useSelectedOperatingGroup } from '../hooks/useSelectedOperatingGroup';

type B52Entry = {
  code: string;
  name: string;
};

type B52AutocompleteProps = {
  b52Code: string;
  b52Name: string;
  onChange: (code: string, name: string) => void;
  labelStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  background: 'white',
  border: '1px solid #dfe6e9',
  borderTop: 'none',
  borderRadius: '0 0 6px 6px',
  boxShadow: '0 6px 18px rgba(0,0,0,.15)',
  zIndex: 9999,
  maxHeight: '180px',
  overflowY: 'auto',
};

const B52Autocomplete: React.FC<B52AutocompleteProps> = ({
  b52Code,
  b52Name,
  onChange,
  labelStyle,
  inputStyle,
}) => {
  const selection = useSelectedOperatingGroup();
  const [loaded, setLoaded] = useState<{ groupId: string | null; entries: B52Entry[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const catalogue = useMemo(() =>
    !selection.changedInAnotherTab && loaded?.groupId === selection.groupId ? loaded.entries : [],
  [loaded, selection.groupId, selection.changedInAnotherTab]);
  const [codeOpen, setCodeOpen] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);
  const codeWrapRef = useRef<HTMLDivElement>(null);
  const nameWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoaded(null);
    setError(null);
    setCodeOpen(false);
    setNameOpen(false);
    if (selection.groupId === null || selection.groupId !== getActiveOperatingGroupId()) return;
    if (selection.changedInAnotherTab) {
      setError('Робочу групу змінено в іншій вкладці. Оновіть сторінку.');
      return;
    }
    let active = true;
    api.get<B52Entry[]>('/b52/catalogue').then((res) => {
      if (active) setLoaded({ groupId: selection.groupId, entries: res.data ?? [] });
    }).catch((failure) => {
      if (active) setError(`Не вдалося завантажити підказки B52: ${failure?.response?.data?.message || failure?.message || 'Невідома помилка'}`);
    });
    return () => { active = false; };
  }, [selection.groupId, selection.changedInAnotherTab]);

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (codeWrapRef.current && !codeWrapRef.current.contains(target)) setCodeOpen(false);
      if (nameWrapRef.current && !nameWrapRef.current.contains(target)) setNameOpen(false);
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, []);

  const codeMatches = useMemo(() => {
    const q = b52Code.trim().toLowerCase();
    return catalogue
      .filter((entry) => !q || entry.code.toLowerCase().includes(q) || entry.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [catalogue, b52Code]);

  const nameMatches = useMemo(() => {
    const q = b52Name.trim().toLowerCase();
    return catalogue
      .filter((entry) => !q || entry.name.toLowerCase().includes(q) || entry.code.toLowerCase().includes(q))
      .slice(0, 8);
  }, [catalogue, b52Name]);

  const selectEntry = (entry: B52Entry) => {
    onChange(entry.code, entry.name);
    setCodeOpen(false);
    setNameOpen(false);
  };

  return (
    <>
      {error && <span role="alert">{error}</span>}
      <label style={labelStyle}>
        Код B52
        <div ref={codeWrapRef} style={{ position: 'relative' }}>
          <input
            style={inputStyle}
            value={b52Code}
            onChange={(e) => { onChange(e.target.value, b52Name); setCodeOpen(true); }}
            onFocus={() => setCodeOpen(true)}
            placeholder="Код для бухгалтера"
            autoComplete="off"
          />
          {codeOpen && codeMatches.length > 0 && (
            <div style={dropdownStyle}>
              {codeMatches.map((entry, idx) => (
                <button
                  key={`${entry.code}-${idx}`}
                  type="button"
                  onClick={() => selectEntry(entry)}
                  style={{ width: '100%', textAlign: 'left', border: 'none', background: 'white', cursor: 'pointer', padding: '6px 10px', fontSize: '12px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}
                >
                  <strong style={{ color: '#2c3e50', flexShrink: 0 }}>{entry.code}</strong>
                  <span style={{ color: '#7f8c8d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </label>

      <label style={labelStyle}>
        Назва B52
        <div ref={nameWrapRef} style={{ position: 'relative' }}>
          <input
            style={inputStyle}
            value={b52Name}
            onChange={(e) => { onChange(b52Code, e.target.value); setNameOpen(true); }}
            onFocus={() => setNameOpen(true)}
            placeholder="Назва для бухгалтера"
            autoComplete="off"
          />
          {nameOpen && nameMatches.length > 0 && (
            <div style={dropdownStyle}>
              {nameMatches.map((entry, idx) => (
                <button
                  key={`${entry.code}-${idx}`}
                  type="button"
                  onClick={() => selectEntry(entry)}
                  style={{ width: '100%', textAlign: 'left', border: 'none', background: 'white', cursor: 'pointer', padding: '6px 10px', fontSize: '12px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}
                >
                  <span style={{ color: '#2c3e50', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</span>
                  <strong style={{ color: '#7f8c8d', flexShrink: 0 }}>({entry.code})</strong>
                </button>
              ))}
            </div>
          )}
        </div>
      </label>
    </>
  );
};

export default B52Autocomplete;
